<template>
	<div>
		<!-- MODAL MODE: Show only if NOT subscribed and NOT inline -->
		<div v-if="!inline && showModal && !notificationsEnabled" class="modal fade show d-block" tabindex="-1" role="dialog"
			style="background: rgba(0,0,0,0.5); z-index: 9999;">
			<div class="modal-dialog modal-dialog-centered" role="document">
				<div class="modal-content border-0 shadow-lg rounded-4">
					<div class="modal-header border-0 pb-0">
						<h5 class="modal-title fw-bold">Enable Notifications</h5>
						<button type="button" class="btn-close" @click="closeModal" aria-label="Close"></button>
					</div>
					<div class="modal-body text-center p-4">
						<div class="mb-4">
							<!-- Using a simple bell icon from FontAwesome or similar if available, or SVG -->
							<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none"
								stroke="#464f82" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
								class="feather feather-bell animate__animated animate__tada">
								<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
								<path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
							</svg>
						</div>
						<h5 class="mb-3">Don't Miss Out!</h5>
						<p class="text-muted">
							Stay updated! Get instant notifications about new orders, status updates, and more directly on
							your device.
						</p>
					</div>
					<div class="modal-footer border-0 justify-content-center pb-4">
						<button type="button" class="btn btn-light rounded-pill px-4 me-2"
							@click="closeModal">Later</button>
						<button type="button" class="btn btn-primary rounded-pill px-4" @click="subscribeUser"
							:disabled="loading" style="background-color: #464f82; border-color: #464f82;">
							<span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
							{{ loading ? 'Subscribing...' : 'Allow Notifications' }}
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- INLINE/MANAGER MODE: Show Toggle for Settings Page -->
		<div v-if="inline" class="card shadow-sm border-0 rounded-3 mb-4">
			<div class="card-body">
				<div class="d-flex justify-content-between align-items-center">
					<div>
						<h6 class="fw-bold mb-1">Push Notifications</h6>
						<p class="text-muted small mb-0">
							{{ notificationsEnabled ? 'You are receiving push notifications on this device.' : 'Enable push notifications to get real-time updates.' }}
						</p>
					</div>
					<div class="form-check form-switch">
						<input class="form-check-input" type="checkbox" role="switch" id="pushNotificationSwitch"
							:checked="notificationsEnabled" @change="toggleSubscription" :disabled="loading"
                             style="width: 3em; height: 1.5em; cursor: pointer;">
                        <label class="form-check-label" for="pushNotificationSwitch">
                             <span v-if="loading" class="spinner-border spinner-border-sm ms-2"></span>
                        </label>
					</div>
				</div>
                
                <div v-if="notificationsEnabled" class="mt-3 text-end">
                     <button @click="testNotification" class="btn btn-sm btn-outline-primary rounded-pill" type="button">
                        <i class="fas fa-paper-plane me-1"></i> Send Test Notification
                     </button>
                </div>
			</div>
		</div>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'PushNotificationModal',
    props: {
        inline: {
            type: Boolean,
            default: false
        }
    },
	data() {
		return {
			showModal: false,
			loading: false,
			notificationsEnabled: false,
			vapidPublicKey: window.vapidPublicKey || process.env.MIX_VAPID_PUBLIC_KEY
		};
	},
	mounted() {
		this.checkSubscription();
        // Listen for changes from other instances (popup vs inline)
        window.addEventListener('push-notification-changed', (event) => {
            if (this.notificationsEnabled !== event.detail.subscribed) {
                this.notificationsEnabled = event.detail.subscribed;
                // If we are the popup and user subscribed elsewhere, hide modal
                if (!this.inline && event.detail.subscribed) {
                    this.showModal = false;
                }
            }
        });
	},
	methods: {
		urlBase64ToUint8Array(base64String) {
			if (!base64String) return null;
			const padding = '='.repeat((4 - base64String.length % 4) % 4);
			const base64 = (base64String + padding)
				.replace(/-/g, '+')
				.replace(/_/g, '/');

			const rawData = window.atob(base64);
			const outputArray = new Uint8Array(rawData.length);

			for (let i = 0; i < rawData.length; ++i) {
				outputArray[i] = rawData.charCodeAt(i);
			}
			return outputArray;
		},
		async checkSubscription() {
			if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
				console.log('Push not supported');
				return;
			}

			// Ensure SW is ready
			try {
				const registration = await navigator.serviceWorker.ready;
				const subscription = await registration.pushManager.getSubscription();

                if (subscription) {
                    // User is already subscribed. 
                    this.notificationsEnabled = true;
                    this.showModal = false;
                    console.log('User is already subscribed.');
                } else {
                    this.notificationsEnabled = false;
                    // Not subscribed, show modal if NOT inline and NOT dismissed
					if (!this.inline && !localStorage.getItem('push_notification_dismissed')) {
						this.showModal = true;
					}
                }
			} catch (e) {
				console.error('SW ready error', e);
			}
		},
        async toggleSubscription(event) {
            // If checked (true) -> Subscribe
            // If unchecked (false) -> Unsubscribe
            if (event.target.checked) {
                await this.subscribeUser(true);
            } else {
                await this.unsubscribeUser();
            }
        },
		async subscribeUser(initial = false) {
			this.loading = true;
			try {
				// Force unregister to clear stale workers
				if (initial) {
					const registrations = await navigator.serviceWorker.getRegistrations();
					for(let registration of registrations) {
						await registration.unregister();
					}
				}
				
				const registration = await navigator.serviceWorker.register('/sw.js');
				await navigator.serviceWorker.ready;

				try {
					// Fetch VAPID key from server (Cache Buster added)
					const keyResponse = await axios.get('/push/vapid-public-key?t=' + new Date().getTime());
					const publicKey = keyResponse.data.publicKey;

					if (!publicKey) throw new Error('No public key returned from server');

					let convertedVapidKey = this.urlBase64ToUint8Array(publicKey.trim());
                    
                    if (convertedVapidKey.length === 66) {
                        convertedVapidKey = convertedVapidKey.sliced ? convertedVapidKey.slice(0, 65) : new Uint8Array(convertedVapidKey.buffer, 0, 65);
                    }

					const subscription = await registration.pushManager.subscribe({
						userVisibleOnly: true,
						applicationServerKey: convertedVapidKey
					});

					// Send subscription to server
					await axios.post('/push/subscribe', subscription.toJSON());

					this.notificationsEnabled = true;
					this.showModal = false; 
                    // Clear dismiss flag on purposeful subscribe
                     localStorage.removeItem('push_notification_dismissed');
                     
                     // Dispatch event to sync other instances (e.g. inline toggle)
                     window.dispatchEvent(new CustomEvent('push-notification-changed', { detail: { subscribed: true } }));
                     
				} catch (subError) {
					console.error('Subscription failed', subError);
					throw subError;
				}

			} catch (error) {
				console.error('Failed to subscribe user: ', error);
                
                // Enhanced permission check for Edge/Chrome
				if (Notification.permission === 'denied' || error.name === 'NotAllowedError') {
					alert('Permission denied. Please enable notifications in your browser settings (Lock icon in address bar).');
				} else {
					alert('Failed to subscribe. Please try again.');
				}
                // Revert switch if failed in inline mode
                if (this.inline) {
                     this.notificationsEnabled = false; 
                }
			} finally {
				this.loading = false;
			}
		},
        async unsubscribeUser() {
            this.loading = true;
            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                
                if (subscription) {
                    // 1. Unsubscribe from Browser (PushManager)
                    await subscription.unsubscribe();
                    
                    // 2. Unsubscribe from Backend (Database)
                    await axios.post('/push/unsubscribe', { endpoint: subscription.endpoint });
                    
                    this.notificationsEnabled = false;
                    console.log('Unsubscribed successfully.');
                    
                    // Dispatch event to sync other instances
                    window.dispatchEvent(new CustomEvent('push-notification-changed', { detail: { subscribed: false } }));
                }
            } catch (error) {
                console.error('Failed to unsubscribe:', error);
                alert('Failed to unsubscribe. Please try again.');
                // Revert state if failed
                this.notificationsEnabled = true;
            } finally {
                this.loading = false;
            }
        },
		closeModal() {
			this.showModal = false;
			// Remind later? Or dismiss permanently? 
			// User said "if not subscribed then show". 
			// I'll set a temporary dismissal (session or short term) or permanent?
			// Usually better to ask again later. 
			localStorage.setItem('push_notification_dismissed', 'true');
		},
		async testNotification() {
			try {
				const response = await axios.get('/test-notification');
                this.$notification('success', response.data);
			} catch (e) {
				console.error(e);
                this.$notification('error', 'Failed to send test notification');
			}
		}
	}
}
</script>

<style scoped>
.modal.fade.show {
	background-color: rgba(0, 0, 0, 0.5);
    display: flex !important;
    align-items: center;
    justify-content: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
}
/* Animation for the switch or other elements if needed */
</style>
